import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Card, CardContent, Typography, TextField, Button, IconButton, Divider,
  FormControlLabel, Switch, Alert, Snackbar, CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import type { Api, TenantView, DocsChrome } from './api';

// Editor for the DOCS site's header nav links + footer. Separate from the
// marketing "Header & Footer" (that edits Site.chrome). Persists to the tenant's
// docs space (all versions) via /tenants/:t/docs-chrome.
type Link = { label: string; href: string; external?: boolean };
type Col = { title: string; links: { label: string; href: string }[] };
type Social = { label: string; href: string };

const EMPTY: Required<DocsChrome> = { headerLinks: [], footerColumns: [], footerNote: '', socials: [] };

export function DocsChromeEditor({ api, tenant, canEdit }: { api: Api; tenant: TenantView; canEdit: boolean }) {
  const [chrome, setChrome] = useState<Required<DocsChrome> | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setChrome(null);
    api.docsChrome(tenant.tenantId)
      .then((r) => setChrome({ ...EMPTY, ...(r.chrome ?? {}) }))
      .catch((e) => { setChrome({ ...EMPTY }); setToast({ msg: e.message, sev: 'error' }); });
  }, [tenant.tenantId]);

  if (!chrome) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>;

  const c = chrome;
  const links = c.headerLinks as Link[];
  const cols = c.footerColumns as Col[];
  const socials = c.socials as Social[];
  const patch = (p: Partial<DocsChrome>) => setChrome((prev) => ({ ...(prev ?? EMPTY), ...p }));

  const upLink = (i: number, p: Partial<Link>) => patch({ headerLinks: links.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const upCol = (ci: number, p: Partial<Col>) => patch({ footerColumns: cols.map((x, j) => (j === ci ? { ...x, ...p } : x)) });
  const upColLink = (ci: number, li: number, p: Partial<{ label: string; href: string }>) =>
    patch({ footerColumns: cols.map((x, j) => (j === ci ? { ...x, links: x.links.map((y, k) => (k === li ? { ...y, ...p } : y)) } : x)) });
  const upSocial = (i: number, p: Partial<Social>) => patch({ socials: socials.map((x, j) => (j === i ? { ...x, ...p } : x)) });

  const save = async () => {
    setBusy(true);
    try {
      const clean: DocsChrome = {
        headerLinks: links.filter((l) => l.label.trim() && l.href.trim()),
        footerColumns: cols.map((x) => ({ title: x.title.trim(), links: x.links.filter((l) => l.label.trim() && l.href.trim()) })).filter((x) => x.title || x.links.length),
        footerNote: c.footerNote.trim() || undefined,
        socials: socials.filter((s) => s.label.trim() && s.href.trim()),
      };
      await api.saveDocsChrome(tenant.tenantId, clean);
      setToast({ msg: 'Saved — live on the docs site within ~30s.', sev: 'success' });
    } catch (e: any) {
      setToast({ msg: e.message === 'invalid_chrome_url' ? 'A link must be an http(s), mailto, tel or relative URL.' : `Couldn’t save: ${e.message}`, sev: 'error' });
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', pb: 8 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          The header links and footer shown on <strong>{tenant.name}</strong>’s documentation site.
        </Typography>
        <Button variant="contained" onClick={save} disabled={!canEdit || busy}>{busy ? 'Saving…' : 'Save'}</Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}><CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Header links</Typography>
        <Typography variant="caption" color="text.secondary">Shown in the docs top bar (desktop). Keep it short — 3–5 links.</Typography>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {links.map((l, i) => (
            <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <TextField label="Label" size="small" value={l.label} disabled={!canEdit} sx={{ flex: 1 }} onChange={(e) => upLink(i, { label: e.target.value })} />
              <TextField label="URL" size="small" value={l.href} disabled={!canEdit} sx={{ flex: 2 }} placeholder="/developer or https://…" onChange={(e) => upLink(i, { href: e.target.value })} />
              <FormControlLabel control={<Switch size="small" checked={!!l.external} disabled={!canEdit} onChange={(e) => upLink(i, { external: e.target.checked })} />} label="New tab" />
              <IconButton size="small" color="error" disabled={!canEdit} onClick={() => patch({ headerLinks: links.filter((_, j) => j !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Button startIcon={<AddIcon />} size="small" disabled={!canEdit} onClick={() => patch({ headerLinks: [...links, { label: '', href: '' }] })} sx={{ alignSelf: 'flex-start' }}>Add link</Button>
        </Stack>
      </CardContent></Card>

      <Card variant="outlined" sx={{ mb: 3 }}><CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Footer columns</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {cols.map((col, ci) => (
            <Box key={ci} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <TextField label="Column title" size="small" value={col.title} disabled={!canEdit} sx={{ flex: 1 }} onChange={(e) => upCol(ci, { title: e.target.value })} />
                <IconButton size="small" color="error" disabled={!canEdit} onClick={() => patch({ footerColumns: cols.filter((_, j) => j !== ci) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Stack>
              <Stack spacing={1} sx={{ pl: 1 }}>
                {col.links.map((l, li) => (
                  <Stack key={li} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <TextField label="Label" size="small" value={l.label} disabled={!canEdit} sx={{ flex: 1 }} onChange={(e) => upColLink(ci, li, { label: e.target.value })} />
                    <TextField label="URL" size="small" value={l.href} disabled={!canEdit} sx={{ flex: 2 }} placeholder="/… or https://…" onChange={(e) => upColLink(ci, li, { href: e.target.value })} />
                    <IconButton size="small" color="error" disabled={!canEdit} onClick={() => upCol(ci, { links: col.links.filter((_, j) => j !== li) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
                <Button startIcon={<AddIcon />} size="small" disabled={!canEdit} onClick={() => upCol(ci, { links: [...col.links, { label: '', href: '' }] })} sx={{ alignSelf: 'flex-start' }}>Add link</Button>
              </Stack>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} size="small" disabled={!canEdit} onClick={() => patch({ footerColumns: [...cols, { title: '', links: [] }] })} sx={{ alignSelf: 'flex-start' }}>Add column</Button>
        </Stack>
      </CardContent></Card>

      <Card variant="outlined"><CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Social links &amp; note</Typography>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {socials.map((s, i) => (
            <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <TextField label="Label" size="small" value={s.label} disabled={!canEdit} sx={{ flex: 1 }} placeholder="Twitter / GitHub…" onChange={(e) => upSocial(i, { label: e.target.value })} />
              <TextField label="URL" size="small" value={s.href} disabled={!canEdit} sx={{ flex: 2 }} placeholder="https://…" onChange={(e) => upSocial(i, { href: e.target.value })} />
              <IconButton size="small" color="error" disabled={!canEdit} onClick={() => patch({ socials: socials.filter((_, j) => j !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Button startIcon={<AddIcon />} size="small" disabled={!canEdit} onClick={() => patch({ socials: [...socials, { label: '', href: '' }] })} sx={{ alignSelf: 'flex-start' }}>Add social</Button>
          <Divider sx={{ my: 1 }} />
          <TextField label="Footer note (copyright / tagline)" size="small" fullWidth value={c.footerNote} disabled={!canEdit} onChange={(e) => patch({ footerNote: e.target.value })} placeholder="© 2026 Your Company. All rights reserved." />
        </Stack>
      </CardContent></Card>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
